/**
 * useCrudPage Hook
 * 
 * Centralized hook for CRUD listing pages (users, groups)
 * Handles:
 * - Data fetching with pagination
 * - Create/Update/Delete mutations
 * - Modal management
 * - Error handling and toasts
 * - Query invalidation
 * 
 * @example
 * const {
 *   data,
 *   isLoading,
 *   pagination,
 *   setPagination,
 *   isModalOpen,
 *   setIsModalOpen,
 *   modalMode,
 *   selectedItem,
 *   setSelectedItem,
 *   mutations: { create, update, delete },
 *   refetch,
 *   handleEdit,
 *   handleDelete,
 *   handleCreate,
 * } = useCrudPage({
 *   queryKey: 'users',
 *   service: userService,
 *   initialPageSize: 10,
 * });
 */

import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from './useToast';
import { createCrudMutation } from '@/utils/crudFactory';

export interface UseCrudPageConfig<T> {
  // Data fetching
  queryKey: string;
  service: {
    getList: (filters: any) => Promise<any>; // Supports both { data: T[]; total: number } and { data: T[]; pagination: { total: number } }
    create: (data: any) => Promise<T>;
    update: (id: string, data: any) => Promise<T>;
    delete: (id: string) => Promise<void>;
  };
  initialPageSize?: number;

  // Messages
  successMessages?: {
    create?: { title: string; message?: string };
    update?: { title: string; message?: string };
    delete?: { title: string; message?: string };
  };
  errorMessages?: {
    fetch?: string;
    create?: string;
    update?: string;
    delete?: string;
  };

  // Callbacks
  onCreateSuccess?: (data: T) => void;
  onUpdateSuccess?: (data: T) => void;
  onDeleteSuccess?: () => void;
}

export interface UseCrudPageReturn<T> {
  // Data
  data: T[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;

  // Pagination
  pagination: { pageIndex: number; pageSize: number };
  setPagination: (pagination: { pageIndex: number; pageSize: number }) => void;

  // Modal
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  modalMode: 'create' | 'edit';
  selectedItem: T | null;
  setSelectedItem: (item: T | null) => void;

  // Mutations
  mutations: {
    create: {
      mutate: (data: any) => void;
      mutateAsync: (data: any) => Promise<T>;
      isPending: boolean;
      isError: boolean;
      error: Error | null;
    };
    update: {
      mutate: (payload: { id: string; data: any }) => void;
      mutateAsync: (payload: { id: string; data: any }) => Promise<T>;
      isPending: boolean;
      isError: boolean;
      error: Error | null;
    };
    delete: {
      mutate: (id: string) => void;
      mutateAsync: (id: string) => Promise<void>;
      isPending: boolean;
      isError: boolean;
      error: Error | null;
    };
  };

  // Handlers
  handleOpenCreate: () => void;
  handleOpenEdit: (item: T) => void;
  handleCancelModal: () => void;
  handleDelete: (id: string) => void;
  handleRefresh: () => void;
}

/**
 * Hook for CRUD listing pages
 * Centralizes fetch, mutations, and modal logic
 */
export const useCrudPage = <T extends { id: string }>(
  config: UseCrudPageConfig<T>
): UseCrudPageReturn<T> => {
  const {
    queryKey,
    service,
    initialPageSize = 10,
    successMessages = {},
    errorMessages = {},
    onCreateSuccess,
    onUpdateSuccess,
    onDeleteSuccess,
  } = config;

  const queryClient = useQueryClient();
  const toast = useToast();

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedItem, setSelectedItem] = useState<T | null>(null);

  /**
   * Fetch data with pagination
   */
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [queryKey, pagination],
    queryFn: () =>
      service.getList({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sort: 'createdAt,desc',
      }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  /**
   * Create mutation
   */
  const createMutation = createCrudMutation({
    mutationFn: (data: any) => service.create(data),
    queryKey,
    successMessage: successMessages.create,
    errorMessage: errorMessages.create || 'Erro ao criar',
    onSuccess: (createdData: any) => {
      setIsModalOpen(false);
      setSelectedItem(null);
      setModalMode('create');
      onCreateSuccess?.(createdData);
    },
    queryClient,
    toast,
  });

  /**
   * Update mutation
   */
  const updateMutation = createCrudMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      service.update(id, data),
    queryKey,
    successMessage: successMessages.update,
    errorMessage: errorMessages.update || 'Erro ao atualizar',
    onSuccess: (updatedData: any) => {
      setIsModalOpen(false);
      setSelectedItem(null);
      setModalMode('create');
      onUpdateSuccess?.(updatedData);
    },
    queryClient,
    toast,
  });

  /**
   * Delete mutation
   */
  const deleteMutation = createCrudMutation({
    mutationFn: (id: string) => service.delete(id),
    queryKey,
    successMessage: successMessages.delete,
    errorMessage: errorMessages.delete || 'Erro ao deletar',
    onSuccess: () => {
      onDeleteSuccess?.();
    },
    queryClient,
    toast,
  });

  /**
   * Modal handlers
   */
  const handleOpenCreate = useCallback(() => {
    setModalMode('create');
    setSelectedItem(null);
    setIsModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((item: T) => {
    setModalMode('edit');
    setSelectedItem(item);
    setIsModalOpen(true);
  }, []);

  const handleCancelModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setModalMode('create');
  }, []);

  /**
   * Delete handler
   */
  const handleDelete = useCallback((id: string) => {
    if (confirm('Tem certeza que deseja deletar?')) {
      deleteMutation.mutate(id);
    }
  }, [deleteMutation]);

  /**
   * Refresh data
   */
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Extract total from response (supports both formats)
  const total = data?.total || data?.pagination?.total || 0;

  return {
    // Data
    data: data?.data || [],
    total,
    isLoading,
    isError,
    error: error as Error | null,
    refetch: handleRefresh,

    // Pagination
    pagination,
    setPagination,

    // Modal
    isModalOpen,
    setIsModalOpen,
    modalMode,
    selectedItem,
    setSelectedItem,

    // Mutations
    mutations: {
      create: {
        mutate: createMutation.mutate,
        mutateAsync: createMutation.mutateAsync,
        isPending: createMutation.isPending,
        isError: createMutation.isError,
        error: createMutation.error as Error | null,
      },
      update: {
        mutate: updateMutation.mutate,
        mutateAsync: updateMutation.mutateAsync,
        isPending: updateMutation.isPending,
        isError: updateMutation.isError,
        error: updateMutation.error as Error | null,
      },
      delete: {
        mutate: deleteMutation.mutate,
        mutateAsync: deleteMutation.mutateAsync,
        isPending: deleteMutation.isPending,
        isError: deleteMutation.isError,
        error: deleteMutation.error as Error | null,
      },
    },

    // Handlers
    handleOpenCreate,
    handleOpenEdit,
    handleCancelModal,
    handleDelete,
    handleRefresh,
  };
};
